/**
 * Curriculum v2 "Crossroads" on the world chooser (docs/CURRICULUM-V2.md §C.1): one crossover per pair
 * of worlds. Hidden silhouette + "Complete <worlds> to open" until one world is complete (teaser: half
 * revealed), open when both are; premium crossovers go through the wish flow like locked nodes.
 */
import { useNavigate } from 'react-router-dom';
import { crossoverStatus, type CrossoverDef, type PathId } from '@sketchling/core';
import { IX, drawingById } from '../lib/content';
import { useLoc, useT } from '../lib/i18n';
import { play } from '../lib/sfx';
import { useAccess, useApp, useProgress } from '../state/store';
import { wishPath } from '../screens/wish/wish';
import { DrawingCardArt } from './DrawingView';
import { Icon } from './icons';
import { toast } from './ui';
import { ShelfRow } from './ShelfRow';
import '../styles/curriculum-v2.css';

export function Crossroads({ onOpen }: { onOpen?: () => void }) {
  const t = useT();
  const loc = useLoc();
  const nav = useNavigate();
  const p = useProgress();
  const access = useAccess();
  const on = useApp((s) => s.config.curriculumV2 && s.config.enableCrossovers);
  const list = [...IX.crossovers.values()];
  if (!on || !list.length) return null;
  const worldName = (w: PathId) => {
    const x = IX.paths.get(w);
    return x ? loc(x.title) : w;
  };
  const missing = (c: CrossoverDef) => c.worlds.filter((w) => !p.completedPaths.includes(w));
  return (
    <section className="crossroads" data-testid="crossroads">
      <h3>
        <Icon name="sparkle" size={18} /> {t('v2.crossroads')}
      </h3>
      <small className="muted">{t('v2.crossroadsBody')}</small>
      <ShelfRow kind="crossroads" trackClassName="v2cr-grid" label={t('v2.crossroads')}>
        {list.map((c) => {
          const st = crossoverStatus(p, c, access);
          const d = drawingById(c.drawingId);
          const miss = missing(c);
          const need = miss.length === 2 ? t('v2.crossAnd', { a: worldName(miss[0]!), b: worldName(miss[1]!) }) : miss.length ? worldName(miss[0]!) : '';
          const mu = `mu-${c.drawingId}`;
          return (
            <button
              key={c.id}
              type="button"
              className={`v2cr-card st-${st}`}
              data-testid={`crossover-${c.id}`}
              data-status={st}
              onClick={() => {
                if (st === 'open' || st === 'done') {
                  play('click');
                  onOpen?.();
                  nav(`/lesson/${mu}?from=/learn`);
                } else if (st === 'premium') {
                  play('click');
                  onOpen?.();
                  nav(wishPath('node', mu, '/learn'));
                } else {
                  play('disabled');
                  toast(t('v2.crossOpen', { world: need }));
                }
              }}
            >
              <span className="v2cr-art">{d && <DrawingCardArt drawing={d} />}</span>
              <b>{st === 'hidden' ? '???' : d ? loc(d.title) : c.id}</b>
              <small>{st === 'done' ? t('v2.crossDone') : st === 'open' || st === 'premium' ? `${worldName(c.worlds[0])} × ${worldName(c.worlds[1])}` : t('v2.crossOpen', { world: need })}</small>
              {(st === 'hidden' || st === 'teaser') && <Icon name="lock" size={16} className="v2cr-lock" />}
              {st === 'premium' && <Icon name="crown" size={16} className="v2cr-lock" />}
            </button>
          );
        })}
      </ShelfRow>
    </section>
  );
}
